import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Box,
  TextField,
  Pagination,
} from "@mui/material";
import { Link } from "react-router-dom";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import axios from "../services/axiosInstance";
import { UserContext } from "../context/UserContext";
import API_BASE_URL from "../../src/config";

const ClassifiedsPage = () => {
  const { user } = useContext(UserContext);
  const [classifieds, setClassifieds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalClassifieds, setTotalClassifieds] = useState(0);
  const [userCity, setUserCity] = useState("");

  useEffect(() => {
    if (user?.id) {
      fetchUserCity(user.id);
    }
  }, [user]);

  const fetchUserCity = async (userId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/${userId}`);
      const city = response.data.interested_city;
      if (city) {
        setUserCity(city);
      }
    } catch (error) {
      console.error("Failed to fetch user city:", error);
    }
  };

  useEffect(() => {
    fetchClassifieds();
  }, [page, search, userCity]);

  const fetchClassifieds = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/classifieds`);
      const allClassifieds = response.data || [];

      const filteredClassifieds = allClassifieds.filter((classified) => {
        const title = classified.title?.toLowerCase() || "";
        const city = classified.city?.toLowerCase() || "";
        const searchLower = search.toLowerCase();
        const userCityLower = userCity.toLowerCase();

        const matchesTitle = title.includes(searchLower);
        const matchesCity = userCity ? city === userCityLower : true;

        return matchesTitle && matchesCity;
      });

      setClassifieds(filteredClassifieds);
      setTotalClassifieds(filteredClassifieds.length);
    } catch (err) {
      console.error("Error fetching classifieds:", err);
      setError("Failed to load classifieds. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Paginate results
  const classifiedsToDisplay = classifieds.slice((page - 1) * 6, page * 6);

  return (
    <Container maxWidth="lg" sx={{ mt: 5, mb: 3 }}>
      <Typography
        variant="h4"
        sx={{ color: "#0653a2" }}
        fontWeight="bold"
        textAlign="center"
        mb={3}
      >
        CityZoom Classifieds
      </Typography>

      <Typography variant="subtitle1" textAlign="center" mb={2}>
        {userCity ? (
          <>
            Showing classifieds in{" "}
            <strong style={{ color: "#0653a2" }}>{userCity}</strong>
          </>
        ) : (
          "Showing all classifieds"
        )}
      </Typography>

      <Typography variant="body1" textAlign="center" sx={{ mb: 4 }}>
        Discover great deals, items for sale, and services in your city.
      </Typography>

      {/* Search filter */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 5, mb: 3 }}>
        <TextField
          label="Search..."
          variant="outlined"
          size="medium"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1); // reset to first page when search changes
          }}
          sx={{ width: 250 }}
        />
      </Box>

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" textAlign="center">
          {error}
        </Typography>
      ) : (
        <>
          <Grid container spacing={3}>
            {classifiedsToDisplay.length > 0 ? (
              classifiedsToDisplay.map((classified) => (
                <Grid item xs={12} sm={6} md={4} key={classified.id}>
                  <Card sx={{ p: 2, boxShadow: 3 }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold">
                        {classified.title}
                      </Typography>

                      <Box display="flex" alignItems="center" mt={1}>
                        <LocationOnIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2">
                          {classified.city}
                        </Typography>
                      </Box>

                      <Box display="flex" alignItems="center" mt={1}>
                        <CurrencyRupeeIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2">
                          ₹{classified.price}
                        </Typography>
                      </Box>

                      <Button
                        variant="contained"
                        sx={{
                          mt: 2,
                          width: "100%",
                          backgroundColor: "#42326e",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "black",
                            color: "white",
                          },
                        }}
                        component={Link}
                        to={`/classifieds/${classified.id}`}
                      >
                        View Details
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Typography
                variant="body1"
                textAlign="center"
                sx={{ width: "100%", mt: 3 }}
              >
                No classifieds available at the moment.
              </Typography>
            )}
          </Grid>

          {/* Pagination */}
          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={Math.ceil(totalClassifieds / 6)}
              page={page}
              onChange={(event, value) => setPage(value)}
              color="primary"
            />
          </Box>
        </>
      )}
    </Container>
  );
};

export default ClassifiedsPage;
